// src/components/admin/PersonalisationImageField.jsx
// Image / video picker used by Portal Personalisation Campaigns: drag & drop / click to upload
// (JPG / PNG / GIF, plus MP4 video when allowVideo is set) or paste a URL. Uploads go to Firebase Storage.
// Videos must be MP4 (H.264 / AAC) so they play on every browser; they are checked before upload.
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../utils/firebase';
import { isVideoUrl, isUnsupportedVideoUrl } from '../../utils/mediaType';
import { validateMp4 } from '../../utils/validateMp4';
import '../../styles/images.css';

const ACCEPTED_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif']
};
const VIDEO_TYPES = { 'video/mp4': ['.mp4'] };
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MB = 1024 * 1024;

function PersonalisationImageField({
  id,
  label,
  helperText,
  placeholder,
  salonId,
  folder,
  value,
  onChange,
  multiple = false,
  maxFiles = 1,
  required = false,
  error,
  allowVideo = false,
  maxVideoMb = 15
}) {
  const images = multiple ? (value || []) : (value ? [value] : []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const [notice, setNotice] = useState('');
  const noun = allowVideo ? 'items' : 'images';
  const videoBytes = maxVideoMb * MB;

  const commit = (next) => onChange(multiple ? next : (next[0] || ''));

  const handleDrop = async (acceptedFiles, rejectedFiles) => {
    setUploadError('');
    setNotice('');
    const problems = [];
    if (rejectedFiles && rejectedFiles.length > 0) {
      problems.push(allowVideo
        ? `Only JPG, PNG, GIF (up to ${MAX_FILE_BYTES / MB}MB) or MP4 video (up to ${maxVideoMb}MB) files are accepted.`
        : `Only JPG, PNG or GIF files up to ${MAX_FILE_BYTES / MB}MB are accepted.`);
    }
    if (acceptedFiles.length === 0) {
      setUploadError(problems.join(' '));
      return;
    }

    const room = multiple ? Math.max(0, maxFiles - images.length) : 1;
    if (room === 0) {
      setUploadError(`You can add up to ${maxFiles} ${noun}. Remove one to add another.`);
      return;
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of acceptedFiles.slice(0, room)) {
        const isVideo = file.type === 'video/mp4';
        try {
          if (isVideo) {
            const { warning } = await validateMp4(file);
            if (warning) setNotice(warning);
          } else if (file.size > MAX_FILE_BYTES) {
            throw new Error(`${file.name} is larger than ${MAX_FILE_BYTES / MB}MB.`);
          }
          const safeName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
          const storageRef = ref(storage, `salons/${salonId || 'default'}/personalisation/${folder}/${safeName}`);
          const snapshot = await uploadBytes(storageRef, file, { contentType: file.type });
          uploaded.push(await getDownloadURL(snapshot.ref));
        } catch (err) {
          console.error('Error uploading personalisation media:', err);
          problems.push(`${file.name}: ${err.message}`);
        }
      }
      if (uploaded.length > 0) commit(multiple ? [...images, ...uploaded] : uploaded);
      if (problems.length > 0) setUploadError(problems.join(' '));
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    accept: allowVideo ? { ...ACCEPTED_TYPES, ...VIDEO_TYPES } : ACCEPTED_TYPES,
    maxSize: allowVideo ? Math.max(MAX_FILE_BYTES, videoBytes) : MAX_FILE_BYTES,
    multiple,
    maxFiles: multiple ? maxFiles : 1
  });

  const handleUseUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') throw new Error('protocol');
    } catch (e) {
      setUrlError(`Please enter a valid ${allowVideo ? 'image or MP4 video' : 'image'} URL starting with https://`);
      return;
    }
    if (isUnsupportedVideoUrl(trimmed) || (!allowVideo && isVideoUrl(trimmed))) {
      setUrlError(allowVideo
        ? 'Only MP4 (H.264) videos are supported, as other formats do not play on every browser.'
        : 'Videos are not supported here.');
      return;
    }
    if (multiple && images.length >= maxFiles) {
      setUrlError(`You can add up to ${maxFiles} ${noun}. Remove one to add another.`);
      return;
    }
    setUrlError('');
    setUrlInput('');
    commit(multiple ? [...images, trimmed] : [trimmed]);
  };

  const removeImage = (index) => {
    commit(images.filter((_, i) => i !== index));
  };

  return (
    <div className="form-group personalisation-image-field">
      <label htmlFor={id}>
        {label}{required && <span className="required-asterisk">*</span>}
      </label>
      {helperText && <p className="form-description">{helperText}</p>}
      {error && <span className="field-error-message">{error}</span>}

      <div {...getRootProps()} className={`dropzone ${error ? 'dropzone-error' : ''}`}>
        <input id={id} {...getInputProps()} />
        {uploading ? <p>{allowVideo ? 'Checking and uploading...' : 'Uploading image...'}</p> : <p>{placeholder}</p>}
      </div>
      {uploadError && <span className="field-error-message">{uploadError}</span>}
      {notice && <div className="form-helper-text">{notice}</div>}

      <div className="personalisation-url-row">
        <input
          type="url"
          value={urlInput}
          onChange={(e) => { setUrlInput(e.target.value); setUrlError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleUseUrl(); } }}
          placeholder={allowVideo ? '…or paste an image or MP4 URL, e.g. https://example.com/video.mp4' : '…or paste an image URL, e.g. https://example.com/image.jpg'}
          aria-label={`${label} URL`}
        />
        <button type="button" className="save-criteria-button" onClick={handleUseUrl} disabled={!urlInput.trim()}>
          Use URL
        </button>
      </div>
      {urlError && <span className="field-error-message">{urlError}</span>}

      {images.length > 0 && (
        <div className="personalisation-image-previews">
          {images.map((src, index) => (
            <div key={`${src}-${index}`} className="personalisation-image-preview">
              {isVideoUrl(src) ? (
                <video src={`${src}#t=0.1`} muted playsInline controls preload="metadata" aria-label={`${label} ${index + 1}`} />
              ) : (
                <img src={src} alt={`${label} ${index + 1}`} />
              )}
              <button type="button" className="remove-button" onClick={() => removeImage(index)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PersonalisationImageField;
